import { createReducer, on } from '@ngrx/store';
import { Project, ProjectCampaignResponse } from '@core/models/projects.model';
import * as ProjectActions from './projects.actions';

export interface ProjectState {
    projects: Project[];
    currentProject: Project | null;
    campaigns: ProjectCampaignResponse | null;
    loading: boolean;
    creating: boolean;
    updating: boolean;
    updatingProjectId: number | null;
    deleting: boolean;
    deletingProjectId: number | null;
    loadingCampaigns: boolean;
    error: string | null;
}

export const initialState: ProjectState = {
    projects: [],
    currentProject: null,
    campaigns: null,
    loading: false,
    creating: false,
    updating: false,
    updatingProjectId: null,
    deleting: false,
    deletingProjectId: null,
    loadingCampaigns: false,
    error: null,
};

export const projectsReducer = createReducer(
    initialState,

    // Load projects
    on(ProjectActions.loadProjects, (state) => ({
        ...state,
        loading: true,
        error: null,
    })),

    on(ProjectActions.loadProjectsSuccess, (state, { projects }) => ({
        ...state,
        projects: projects.map((p) => new Project(p)),
        loading: false,
        error: null,
    })),

    on(ProjectActions.loadProjectsFailure, (state, { error }) => ({
        ...state,
        loading: false,
        error,
        projects: [],
    })),

    // Create project
    on(ProjectActions.createProject, (state) => ({
        ...state,
        creating: true,
        error: null,
    })),

    on(ProjectActions.createProjectSuccess, (state) => ({
        ...state,
        creating: false,
        error: null,
    })),

    on(ProjectActions.createProjectFailure, (state, { error }) => ({
        ...state,
        creating: false,
        error,
    })),

    // Update project
    on(ProjectActions.updateProject, (state, { projectId }) => ({
        ...state,
        updating: true,
        updatingProjectId: projectId,
        error: null,
    })),

    on(ProjectActions.updateProjectSuccess, (state) => ({
        ...state,
        updating: false,
        updatingProjectId: null,
        error: null,
    })),

    on(ProjectActions.updateProjectFailure, (state, { error }) => ({
        ...state,
        updating: false,
        updatingProjectId: null,
        error,
    })),

    // Load campaigns
    on(ProjectActions.loadProjectCampaigns, (state) => ({
        ...state,
        loadingCampaigns: true,
        error: null,
    })),

    on(ProjectActions.loadProjectCampaignsSuccess, (state, { campaigns }) => ({
        ...state,
        campaigns,
        loadingCampaigns: false,
        error: null,
        // Update current project with campaign data
        currentProject: state.currentProject
            ? (() => {
                  const project = new Project(state.currentProject);
                  project.setCampaignData(campaigns);
                  return project;
              })()
            : null,
    })),

    on(ProjectActions.loadProjectCampaignsFailure, (state, { error }) => ({
        ...state,
        loadingCampaigns: false,
        error,
        campaigns: null,
    })),

    on(ProjectActions.clearProjectCampaigns, (state) => ({
        ...state,
        campaigns: null,
        currentProject: state.currentProject ? new Project(state.currentProject) : null,
    })),

    // Delete project
    on(ProjectActions.deleteProject, (state, { projectId }) => ({
        ...state,
        deleting: true,
        deletingProjectId: projectId,
        error: null,
    })),

    on(ProjectActions.deleteProjectSuccess, (state, { projectId }) => ({
        ...state,
        deleting: false,
        deletingProjectId: null,
        error: null,
        projects: state.projects.filter((project) => project.id !== projectId),
    })),

    on(ProjectActions.deleteProjectFailure, (state, { error }) => ({
        ...state,
        deleting: false,
        deletingProjectId: null,
        error,
    })),

    // Set current project
    on(ProjectActions.setCurrentProject, (state, { project }) => ({
        ...state,
        currentProject: project ? new Project(project) : null,
    })),

    on(ProjectActions.clearSelectedProject, (state) => ({
        ...state,
        currentProject: null,
    })),

    // Clear projects
    on(ProjectActions.clearProjects, () => ({
        ...initialState,
    })),
);
